/* eslint-disable camelcase */
import { useQueryClient } from "@tanstack/react-query"
import { useEffect, useState, useContext } from "react"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import { useNavigate, useLocation } from "react-router-dom"
import {
  Box,
  Paper,
  TextField,
  Tooltip,
  IconButton,
  Grid,
  Typography,
  Divider,
  Chip,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useMutation, useQuery, api, showWarning } from "../../../api"
import { GlobalContext } from "../../../contexts/GlobalContext"
import getIconComponent from "../../utils/getIconComponent"
import Save from "@mui/icons-material/Save"

import DetalleArticulosInvGrid from "./components/DetalleArticulosInvGrid"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1400px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const ProcesarSolicitudInv = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { state } = useLocation()
  const { selectedMenuInfo } = useContext(GlobalContext)

  const [modoProcesamiento, setModoProcesamiento] = useState("")

  const [formData, setFormData] = useState({
    solnumero: "",
    sgastatus: "",
    tsolInEg: "",
    tsolcodigo: "",
    sgadescri: "",
    locsolicita: "",
    bodegreso: "",
    bodingreso: "",
    tracodigo: "",
    tratipo: "",
    trasigno: "",
    trametodo: 0,
    detalle: [],
  })

  useEffect(() => {
    if (!state?.solnumero) {
      navigate(-1)
    }
  }, [state, navigate])

  let locUsuarioActual = ""
  try {
    const jwtDataStr = localStorage.getItem("jwt")
    if (jwtDataStr) {
      const jwtData = JSON.parse(jwtDataStr)
      locUsuarioActual = jwtData?.localidad?.loccodigo || ""
    }
  } catch (error) {
    console.error("Error al leer los datos de localidad del usuario:", error)
  }

  const { data: solData, isLoading: loadSol } = useQuery({
    queryKey: ["getDetalleSolicitudInvEdit", state?.solnumero],
    queryFn: async () => {
      try {
        const payload = { solnumero: state.solnumero }
        const response = await api.post("/IngEgrporSolicitud/getDetalleSolicitudInv", payload)

        let coreData = null
        const r1 = response?.data
        if (r1?.data?.data?.solnumero) coreData = r1.data.data
        else if (r1?.data?.solnumero) coreData = r1.data
        else if (r1?.solnumero) coreData = r1

        return coreData
      } catch (error) {
        console.error("Error al cargar la solicitud:", error)
        return null
      }
    },
    enabled: !!state?.solnumero,
  })

  const { data: catData, isLoading: loadCatLog } = useQuery({
    queryKey: ["catalogosLogisticosEdit", solData?.clicodigo || ""],
    queryFn: async () => {
      try {
        const payload = solData?.clicodigo ? { clicodigo: solData.clicodigo } : {}
        const response = await api.post("/CrearSolicitudIngEgr/getCatalogosLogisticos", payload)
        let catalogos = {}
        const res = response?.data?.data || response?.data || response || {}
        if (res.bodegas) catalogos = res
        else if (res.data && res.data.bodegas) catalogos = res.data
        else if (response?.data?.data?.data?.bodegas) catalogos = response.data.data.data
        return catalogos
      } catch (error) {
        return {}
      }
    },
    enabled: !!solData,
  })

  const extractArray = (rawList) =>
    Array.isArray(rawList) ? rawList : Array.isArray(rawList?.data) ? rawList.data : []
  const listaBodegas = extractArray(catData?.bodegas)

  // =======================================================================
  // INICIALIZACIÓN DEL MODO DE PROCESAMIENTO
  // =======================================================================
  useEffect(() => {
    if (solData && !modoProcesamiento) {
      // SOLO SOLEGRLOC es transferencia asíncrona (2 pasos con Radio Buttons)[cite: 7]
      const isTransferenciaAsincrona = ["SOLEGRLOC"].includes(String(solData.tsolInEg).trim())
      let modoDefecto = solData.tratipo || "EGRESO"

      if (isTransferenciaAsincrona) {
        if (locUsuarioActual === solData.locsolicita) {
          modoDefecto = "INGRESO"
        } else {
          modoDefecto = "EGRESO"
        }
      }
      setModoProcesamiento(modoDefecto)
    }
  }, [solData])

  // =======================================================================
  // LÓGICA DE AUDITORÍA, BLOQUEOS Y CÁLCULOS EXACTOS
  // =======================================================================
  useEffect(() => {
    if (!solData || !modoProcesamiento) return

    const mnem = String(solData.tsolInEg).trim()
    const isTransferenciaAsincrona = ["SOLEGRLOC"].includes(mnem)
    const isBodegasInternas = mnem === "SOLEGRBOD"

    // REGLAS 1, 2, 3 y 4: Bloquear bodega en la grilla y heredar de la cabecera
    const disableBodegaGrid = ["SOLINGAJU", "SOLEGRAJU", "SOLEGRPRES", "SOLEGRBOD"].includes(mnem)

    const bodegaFallback = modoProcesamiento === "INGRESO" ? solData.bodingreso : solData.bodegreso

    setFormData((prev) => ({
      ...prev,
      solnumero: solData.solnumero || "",
      sgastatus: solData.sgastatus || "",
      tsolInEg: solData.tsolInEg || "",
      tsolcodigo: solData.tsolcodigo || "",
      sgadescri: solData.sgadescri || "",
      locsolicita: solData.locsolicita || "",
      bodegreso: solData.bodegreso || "",
      bodingreso: solData.bodingreso || "",
      tracodigo: solData.tracodigo || "",
      trasigno: solData.trasigno || "",
      trametodo: solData.trametodo || 0,
      tratipo: modoProcesamiento,
      detalle: (solData.detalle || []).map((item) => {
        const solicitada = parseFloat(item.sgacansol || 0)
        const egresada = parseFloat(item.sgacanegr || 0)
        const ingresada = parseFloat(item.sgacanrec || 0)

        const pend_egreso = Math.max(0, solicitada - egresada)
        const pend_ingreso = Math.max(0, egresada - ingresada)

        let cant_pendiente_activa = 0

        if (isTransferenciaAsincrona) {
          if (modoProcesamiento === "EGRESO") {
            cant_pendiente_activa = pend_egreso
          } else if (modoProcesamiento === "INGRESO") {
            cant_pendiente_activa = pend_ingreso
          }
        } else {
          const procesada = modoProcesamiento === "INGRESO" ? ingresada : egresada
          cant_pendiente_activa = Math.max(0, solicitada - procesada)
        }

        const prevItem = prev.detalle?.find((d) => d.artcodigo === item.artcodigo)

        // REGLA 4: Para Transferencias Internas (SOLEGRBOD), pre-llenar la cantidad a afectar con la pendiente
        let cant_afectar = 0
        if (prevItem && prevItem.cant_afectar !== undefined) {
          cant_afectar = parseFloat(prevItem.cant_afectar || 0)
        } else if (isBodegasInternas) {
          cant_afectar = cant_pendiente_activa
        }

        if (cant_afectar > cant_pendiente_activa) cant_afectar = 0

        return {
          ...item,
          pend_egreso,
          pend_ingreso,
          cant_pendiente: cant_pendiente_activa,
          // Si el grid bloquea la bodega, inyectamos obligatoriamente la de la cabecera
          bodcodigo: disableBodegaGrid ? bodegaFallback : prevItem?.bodcodigo || bodegaFallback,
          cant_afectar,
          lotes: prevItem?.lotes || [],
          series: prevItem?.series || [],
        }
      }),
    }))
  }, [solData, modoProcesamiento])

  const { mutateAsync: ProcesarTransaccion, isPending: isSaving } = useMutation({
    queryKey: ["procesarTransaccionInv"],
    fn: async (data) => {
      const response = await api.post("/IngEgrporSolicitud/procesarTransaccionInv", data)
      return response.data
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: async () => {
      await queryClient.invalidateQueries()
      navigate(-1)
    },
  })

  const handleSubmit = async (e) => {
    if (e) e.preventDefault()

    if (!formData.solnumero) return showWarning("No se encontró el número de solicitud.")

    const itemsAProcesar = formData.detalle.filter((item) => parseFloat(item.cant_afectar) > 0)

    if (itemsAProcesar.length === 0) {
      return showWarning("Debe ingresar la cantidad a afectar en al menos un artículo para procesar el movimiento.")
    }

    for (const item of itemsAProcesar) {
      const cantAfectar = parseFloat(item.cant_afectar)

      if (!item.bodcodigo || String(item.bodcodigo).trim() === "") {
        return showWarning(`Debe asignar una bodega para el artículo ${item.artcodigo}.`)
      }

      if (item.artlote !== 0) {
        const totLotes = item.lotes.reduce((acc, curr) => acc + parseFloat(curr.cantidad || 0), 0)
        if (totLotes !== cantAfectar) {
          return showWarning(
            `El artículo ${item.artcodigo} maneja lotes. La suma de las cantidades en los lotes (${totLotes}) debe ser exactamente igual a la cantidad a procesar (${cantAfectar}).`,
          )
        }
      }

      if (item.artserie !== 0) {
        if (item.series.length !== cantAfectar) {
          return showWarning(
            `El artículo ${item.artcodigo} maneja series. Usted indicó que afectará ${cantAfectar} unidades, pero ha registrado ${item.series.length} series. Deben coincidir exactamente.`,
          )
        }
      }
    }

    try {
      const payloadToSend = { ...formData, detalle: formData.detalle }

      if (payloadToSend.tratipo === "INGRESO") {
        payloadToSend.bodingreso = payloadToSend.bodingreso || itemsAProcesar[0].bodcodigo
      } else {
        payloadToSend.bodegreso = payloadToSend.bodegreso || itemsAProcesar[0].bodcodigo
      }

      await ProcesarTransaccion(payloadToSend)
    } catch (error) {
      console.error("Error al procesar la transacción:", error)
    }
  }

  const grabarAction = selectedMenuInfo?.data?.barraAcciones?.find((action) => action?.acccaption === "GRABAR")
  const toolbarActions = grabarAction
    ? [
        {
          label: grabarAction.acccaption,
          key: grabarAction.acccaption,
          icon: getIconComponent(grabarAction.accnameicono, grabarAction.acctipoico),
        },
      ]
    : [{ label: "Grabar", key: "GRABAR", icon: <Save /> }]

  const isLoadingGlobal = loadSol || isSaving || loadCatLog

  // =======================================================================
  // REGLA DE NEGOCIO: RESTRICCIÓN DE FASE (SOLO APLICA A SOLEGRLOC)
  // =======================================================================
  const mnem_eval = String(formData.tsolInEg).trim()
  const isTransferenciaAsincrona = ["SOLEGRLOC"].includes(mnem_eval)
  const disableBodegaGrid = ["SOLINGAJU", "SOLEGRAJU", "SOLEGRPRES", "SOLEGRBOD"].includes(mnem_eval)

  let disableEgreso = false
  let disableIngreso = false

  if (isTransferenciaAsincrona && locUsuarioActual && formData.locsolicita) {
    if (locUsuarioActual === formData.locsolicita) {
      disableEgreso = true
    } else {
      disableIngreso = true
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        <Box sx={{ mb: 2 }}>
          {toolbarActions.map((action) => (
            <Tooltip title={action.label} key={action.key}>
              <IconButton
                onClick={handleSubmit}
                disabled={isLoadingGlobal}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, bgcolor: "white" }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Procesar Transacción: {state?.solnumero}</b>
          {formData.tratipo && (
            <Chip
              label={formData.tratipo === "INGRESO" ? "RECEPCIÓN" : "DESPACHO"}
              color={formData.tratipo === "INGRESO" ? "secondary" : "primary"}
              sx={{ ml: 2, fontWeight: "bold", fontSize: "1.1rem", py: 2.5, px: 2 }}
            />
          )}
        </div>

        <CustomBackdrop isLoading={isLoadingGlobal} />

        <Box sx={StyledRoot}>
          <Paper
            elevation={3}
            sx={{ p: 4, borderRadius: 3, background: "white" }}
            component="form"
            onSubmit={handleSubmit}
          >
            <Typography variant="h6" color="primary" gutterBottom>
              Datos de Cabecera (Solo Lectura)
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={3}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  disabled
                  label="Tipo Solicitud"
                  value={formData.tsolInEg}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  disabled
                  label="Transacción (Método)"
                  value={`${formData.tracodigo} - ${formData.tratipo} (${formData.trasigno})`}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  disabled
                  label="Localidad Solicita"
                  value={formData.locsolicita || "N/A"}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  disabled
                  label={formData.tratipo === "INGRESO" ? "Bodega de Ingreso" : "Bodega de Egreso"}
                  value={formData.tratipo === "INGRESO" ? formData.bodingreso || "N/A" : formData.bodegreso || "N/A"}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  disabled
                  multiline
                  label="Observación / Descripción"
                  value={formData.sgadescri}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h6" color="primary" gutterBottom>
              Detalle de Artículos a Procesar
            </Typography>

            {/* CONTROLADOR DE FASE DE TRANSFERENCIA VISUAL */}
            {isTransferenciaAsincrona && (
              <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                <Paper
                  elevation={1}
                  sx={{
                    p: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 3,
                    backgroundColor: "#e3f2fd",
                    border: "1px solid #90caf9",
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    FASE A PROCESAR:
                  </Typography>
                  <RadioGroup row value={modoProcesamiento} onChange={(e) => setModoProcesamiento(e.target.value)}>
                    <FormControlLabel
                      value="EGRESO"
                      control={<Radio />}
                      label={<Typography fontWeight="bold">1. DESPACHO (Egreso)</Typography>}
                      disabled={disableEgreso}
                    />
                    <FormControlLabel
                      value="INGRESO"
                      control={<Radio />}
                      label={<Typography fontWeight="bold">2. RECEPCIÓN (Ingreso)</Typography>}
                      disabled={disableIngreso}
                    />
                  </RadioGroup>
                </Paper>
              </Box>
            )}

            {formData.solnumero ? (
              <DetalleArticulosInvGrid
                detalle={formData.detalle}
                setFormData={setFormData}
                tratipo={formData.tratipo}
                bodcodigo={formData.tratipo === "INGRESO" ? formData.bodingreso : formData.bodegreso}
                sgastatus={formData.sgastatus}
                tsolInEg={formData.tsolInEg}
                listaBodegas={listaBodegas}
                disableBodegaGrid={disableBodegaGrid} // Nueva directiva para el componente hijo
              />
            ) : (
              <Typography variant="body2" color="textSecondary" sx={{ fontStyle: "italic", mt: 2 }}>
                Cargando artículos...
              </Typography>
            )}
          </Paper>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default ProcesarSolicitudInv
